/**
 * SEEK BAR — the transport's progress bar, made a real control.
 *
 * It was a div: an outer track with an absolutely-positioned fill, no role, no tab
 * stop and no handlers, and TransportBar's header said so — "the scrubber is not
 * seekable because there is nothing to seek". That was true of the audio and false of
 * the position: `elapsed` is a number in playerStore that the waveform, the clock, the
 * timeline playhead and this bar all render from, and moving it moves all four. So
 * this seeks that. What it cannot do is make sound come out at the new position,
 * because there is no audio yet — see the SIMULATED note at the bottom.
 *
 * ONE STATE, NOT TWO. The drawn position always comes from the `value` prop, on every
 * frame including mid-drag: the pointer handlers call `onSeek`, the store updates, and
 * the bar re-renders from it. There is no local copy of the position to fall out of
 * step with the player, which is what "do not create a second player state" rules out.
 * The only local state is `dragging`, which is presentation — it enlarges the thumb
 * and, more importantly, turns off the transition that exists to smooth the playback
 * tick and would otherwise make the fill trail behind the finger. That transition is
 * one tick long, derived from `speed` rather than fixed at a second, so it stays in
 * step with the clock when the rate changes — see lib/playback.
 *
 * POINTER EVENTS, not mouse plus touch. One set of handlers covers mouse, touch and
 * pen, and `setPointerCapture` is what makes a drag continue when the pointer leaves
 * the 4px-tall bar — which it does immediately, because nobody drags along a hairline.
 * Capture also delivers the final pointerup wherever it happens, so a drag that ends
 * off the edge of the window still commits.
 *
 * SCROLLING WHILE DRAGGING is prevented by `touch-action: none` rather than by
 * calling preventDefault on touchmove. The CSS tells the browser up front that this
 * element consumes pans, so the gesture is never claimed by the scroller in the first
 * place; preventDefault only works if the listener is non-passive, which React's are
 * not. This is the whole of the brief's "prevent accidental page scrolling while
 * dragging on touch devices" — and it is scoped to this element, so the page still
 * scrolls normally everywhere around it.
 *
 * A hit area larger than the line: the bar is 4px of ink inside a 16px target with
 * transparent padding, so a tap lands on it with a fingertip. Without this the control
 * is technically draggable and practically unusable on a phone.
 *
 * ARIA: role="slider" with min/max in seconds, `aria-valuetext` reading as a time
 * rather than "134", and the arrow/Home/End keys a slider is expected to answer.
 *
 * SIMULATED. Seeking moves the playback position that the rest of the Studio reads —
 * clock, waveform, timeline playhead — and nothing more, because there is no <audio>
 * element to move. When one exists, `onSeek` is where its currentTime is set; nothing
 * about this component changes.
 */
import { useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { formatTime } from '../../lib/formatting';
import { smoothingSeconds } from '../../lib/playback';
import { c } from '../../styles/theme';

export interface SeekBarProps {
  /** Current position in seconds. The single source of truth for what is drawn. */
  value: number;
  /** Seconds buffered ahead from the start of the media, when known. */
  bufferedSec?: number;
  /** Total length in seconds. A zero/absent duration makes the bar read-only. */
  duration: number;
  /** Seeks to the given second. Already clamped to 0…duration. */
  onSeek: (seconds: number) => void;
  /** Accessible name. Defaults to the generic one; pass the song for context. */
  label?: string;
  /** Track height in pixels. The transport uses the default; fullscreen is thicker. */
  height?: number;
  /** True while playback is running, which is the only time the fill animates. */
  playing?: boolean;
  /**
   * Playback rate, so the smoothing transition covers exactly one tick.
   *
   * Defaults to 1×, which reproduces the `1s linear` this replaced — a caller that does
   * not know about speed behaves exactly as before. Passing it matters at 2×: the clock
   * then advances every 500ms, and a fill still transitioning over a full second would
   * arrive after the next value had been set and read permanently behind the readout
   * beside it.
   */
  speed?: number;
  style?: CSSProperties;
}

/** Arrow-key step, in seconds. */
const STEP = 5;
/** PageUp/PageDown step, for crossing a long track quickly. */
const PAGE_STEP = 15;

export default function SeekBar({
  value,
  bufferedSec = 0,
  duration,
  onSeek,
  label = 'Seek',
  height = 4,
  playing = false,
  speed = 1,
  style,
}: SeekBarProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);

  /*
    One tick's worth of smoothing, shared by the fill and the thumb so they cannot
    disagree about where the playhead is. Computed once rather than twice for that
    reason — two calls would be two chances to pass a different speed.
  */
  const smooth = `${smoothingSeconds(speed)}s linear`;

  // A track with no known length cannot be seeked into meaningfully: every position
  // would map to the same place. It still renders, as an empty bar.
  const seekable = duration > 0;
  const pos = Math.max(0, Math.min(duration, value));
  const buffered = Math.max(pos, Math.min(duration, bufferedSec));
  const pct = seekable ? (pos / duration) * 100 : 0;
  const bufferedPct = seekable ? (buffered / duration) * 100 : 0;

  /** Maps a viewport x to a position in seconds, clamped to the track. */
  function secondsAt(clientX: number): number {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return pos;
    const ratio = (clientX - rect.left) / rect.width;
    // Clamped here rather than by the caller, so every entry point — click, drag,
    // and a drag that ends past the edge of the window — is bounded by construction.
    return Math.round(Math.max(0, Math.min(1, ratio)) * duration);
  }

  function seekTo(clientX: number) {
    const next = secondsAt(clientX);
    if (next !== pos) onSeek(next);
  }

  /** Nudges by a number of seconds, clamped. Shared by every key binding. */
  function nudge(delta: number) {
    const next = Math.max(0, Math.min(duration, pos + delta));
    if (next !== pos) onSeek(next);
  }

  return (
    <div
      ref={trackRef}
      role="slider"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={duration}
      aria-valuenow={pos}
      // "1:22 of 4:05" rather than the raw second count, which is what a screen
      // reader would otherwise announce on every arrow press.
      aria-valuetext={`${formatTime(pos)} of ${formatTime(duration)}`}
      aria-disabled={seekable ? undefined : true}
      // A slider is expected to be in the tab order; `-1` when there is nothing to
      // seek, so it is not a stop that does nothing.
      tabIndex={seekable ? 0 : -1}
      className="lyx-seek"
      onPointerDown={(e) => {
        if (!seekable) return;
        // Left button only for a mouse. `buttons` rather than `button`, because a
        // right-press should open the context menu, not scrub.
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        // Focus follows the press, so the arrow keys work straight after a click
        // without a second tab. Buttons do this natively; a div does not.
        e.currentTarget.focus();
        // Guarded because jsdom does not implement pointer capture. Without capture
        // the drag would stop the instant the pointer left the 4px track.
        e.currentTarget.setPointerCapture?.(e.pointerId);
        setDragging(true);
        seekTo(e.clientX);
      }}
      onPointerMove={(e) => {
        // Only while dragging: a bare hover must not move the playhead.
        if (dragging) seekTo(e.clientX);
      }}
      onPointerUp={(e) => {
        if (!dragging) return;
        setDragging(false);
        // The release position is committed explicitly rather than relying on the
        // last pointermove — a quick drag can end between two move events, and the
        // brief's requirement is the position where the finger came up.
        seekTo(e.clientX);
      }}
      onPointerCancel={() => setDragging(false)}
      onKeyDown={(e) => {
        if (!seekable) return;
        // Left/Down back, Right/Up forward — the conventional pairing for a
        // horizontal slider, so the vertical keys are not dead.
        const delta =
          e.key === 'ArrowLeft' || e.key === 'ArrowDown'
            ? -STEP
            : e.key === 'ArrowRight' || e.key === 'ArrowUp'
              ? STEP
              : e.key === 'PageDown'
                ? -PAGE_STEP
                : e.key === 'PageUp'
                  ? PAGE_STEP
                  : null;
        if (delta !== null) {
          e.preventDefault();
          nudge(delta);
          return;
        }
        if (e.key === 'Home') {
          e.preventDefault();
          onSeek(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          onSeek(duration);
        }
      }}
      style={{
        // The hit area. Transparent padding around the visible line, so the target
        // is a fingertip tall while the design stays a hairline.
        position: 'relative',
        padding: '6px 0',
        cursor: seekable ? 'pointer' : 'default',
        // See the header: this is what stops the page scrolling under a drag.
        touchAction: 'none',
        ...style,
      }}
    >
      {/* The track. Its own element rather than a background on the hit area, because
          the padding above must not be part of the bar. */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height,
          background: c.surface3,
          borderRadius: 4,
          overflow: 'visible',
        }}
      >
        <div
          aria-hidden="true"
          className="lyx-seek-buffered"
          style={{
            position: 'absolute',
            inset: '0 auto 0 0',
            width: `${bufferedPct}%`,
            background: c.accentSoft,
            borderRadius: 4,
            transition: 'width 0.15s linear',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: '0 auto 0 0',
            width: `${pct}%`,
            background: c.accentFill,
            borderRadius: 4,
            /*
              One tick, linear, so the fill reads as continuous rather than stepping —
              and it tracks the playback rate rather than being a hard-coded second,
              which is what keeps it in step with the clock at 1.5× and 2×.

              Dropped while dragging and while paused: mid drag it would lag behind the
              finger, and after a click-to-seek on a paused track it would crawl to the
              new position instead of arriving.
            */
            transition: dragging || !playing ? 'none' : `width ${smooth}`,
          }}
        />

        {/* The playhead. Present at every width — it is what says the bar can be
            grabbed — and it grows under the finger so the contact point stays visible
            while dragging. */}
        {seekable && (
          <div
            aria-hidden="true"
            className="lyx-seek-thumb"
            style={{
              position: 'absolute',
              top: '50%',
              left: `${pct}%`,
              width: dragging ? 13 : 10,
              height: dragging ? 13 : 10,
              borderRadius: '50%',
              background: c.accentFill,
              transform: 'translate(-50%, -50%)',
              transition: dragging || !playing ? 'none' : `left ${smooth}`,
              // The halo doubles as the visual for the grab state.
              boxShadow: dragging ? `0 0 0 5px ${c.accentSoft}` : `0 0 0 3px ${c.accentSoft}`,
            }}
          />
        )}
      </div>
    </div>
  );
}
